/** An inclusive range of calendar dates, both ends `YYYY-MM-DD`. */
export interface DateRange {
  startDate: string;
  endDate: string;
}

export type DatePreset =
  | 'last_7_days'
  | 'last_14_days'
  | 'last_30_days'
  | 'this_month'
  | 'last_month'
  | 'flight_to_date'
  | 'custom';

const MS_PER_DAY = 86_400_000;

/** Parsed as UTC so the result never shifts a day for a user west of UTC. */
function parse(date: string): Date {
  return new Date(`${date}T00:00:00Z`);
}

function format(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MS_PER_DAY);
}

/**
 * Resolves a preset against a caller-supplied "today".
 *
 * Every range ends YESTERDAY, never today: the ad platforms restate the current day for
 * hours, so including it makes the dashboard disagree with itself between refreshes and
 * always understates the last bar on the chart.
 *
 * `flight_to_date` and `custom` are resolved by the caller — one needs the project's
 * campaigns, the other needs the user's own choice.
 */
export function resolvePreset(
  preset: Exclude<DatePreset, 'custom' | 'flight_to_date'>,
  today: string,
): DateRange {
  const yesterday = addDays(parse(today), -1);

  switch (preset) {
    case 'last_7_days':
      return { startDate: format(addDays(yesterday, -6)), endDate: format(yesterday) };
    case 'last_14_days':
      return { startDate: format(addDays(yesterday, -13)), endDate: format(yesterday) };
    case 'last_30_days':
      return { startDate: format(addDays(yesterday, -29)), endDate: format(yesterday) };
    case 'this_month': {
      const today_ = parse(today);
      const firstOfThisMonth = new Date(Date.UTC(today_.getUTCFullYear(), today_.getUTCMonth(), 1));
      // On the 1st, yesterday belongs to last month and no day of this month has
      // completed — so the range is that single completed day, not an inverted range.
      return {
        startDate: format(firstOfThisMonth > yesterday ? yesterday : firstOfThisMonth),
        endDate: format(yesterday),
      };
    }
    case 'last_month': {
      const today_ = parse(today);
      const firstOfThisMonth = new Date(Date.UTC(today_.getUTCFullYear(), today_.getUTCMonth(), 1));
      const lastOfPrevMonth = addDays(firstOfThisMonth, -1);
      const firstOfPrevMonth = new Date(
        Date.UTC(lastOfPrevMonth.getUTCFullYear(), lastOfPrevMonth.getUTCMonth(), 1),
      );
      return { startDate: format(firstOfPrevMonth), endDate: format(lastOfPrevMonth) };
    }
  }
}

/** Inclusive day count, so a one-day range counts 1. */
export function dayCount(range: DateRange): number {
  return (
    Math.round((parse(range.endDate).getTime() - parse(range.startDate).getTime()) / MS_PER_DAY) + 1
  );
}
